import type { AgCartesianCrossLineOptions } from 'ag-charts-community'

/**
 * The deal-quality colour ramp, as hue knots pinned to the tier thresholds.
 *
 * Dots and the background wash are both derived from this one table — the dot at
 * full saturation, the ground behind it at a light wash — which is the whole
 * point of pinning the knots to the thresholds. Interpolating one ramp
 * end-to-end instead would let the ramp's own geometry decide where yellow
 * lands: green→orange linear in hue reaches yellow at ratio ~0.73, while the
 * yellow zone starts at 0.90, so dots would sit on ground of a different colour
 * for a seventh of the scale.
 *
 * Hues are HSV, which is how the ramp was specified; `hsvToCss` converts.
 */
export const HUE_KNOTS: { hue: number; ratio: number }[] = [
  { hue: 110, ratio: 0.6 },
  { hue: 80, ratio: 0.8 },
  { hue: 60, ratio: 0.9 },
  { hue: 40, ratio: 1.1 },
]

/**
 * Where the ramp stops climbing: FMV +10%, the last knot. Above it every listing
 * keeps that knot's orange rather than dropping out of the scale, which mirrors
 * the flat green below the first knot.
 */
export const RAMP_TOP_RATIO = 1.1

/** Full-strength dot fill, and the darker value its outline is drawn at. */
export const DOT_SATURATION = 60
export const DOT_VALUE = 80
const DOT_STROKE_VALUE = 40
/** Light wash behind the dots, so it never competes with them. */
export const BAND_SATURATION = 20
export const BAND_VALUE = 98
const BAND_OPACITY = 0.15

/**
 * Hue for a price-to-FMV ratio. Every ratio has one — the ramp is flat past
 * either end rather than stopping.
 *
 * Flat below the first knot because the top tier runs from 0.002, and a ramp
 * that kept climbing would spend most of its range separating two listings that
 * are both simply excellent. Flat above the last knot for the mirror-image
 * reason: past FMV +10% a listing is simply not a deal, and how far past tells a
 * buyer nothing they would act on. The percentage in the tooltip says it exactly
 * for anyone who wants the number.
 */
export const dealToHue = (ratio: number): number => {
  let previous = HUE_KNOTS[0]
  if (!previous) return 0
  if (ratio <= previous.ratio) return previous.hue

  for (const knot of HUE_KNOTS.slice(1)) {
    if (ratio <= knot.ratio) {
      const span = knot.ratio - previous.ratio
      const progress = span === 0 ? 0 : (ratio - previous.ratio) / span
      return previous.hue + progress * (knot.hue - previous.hue)
    }
    previous = knot
  }
  return previous.hue
}

const round = (value: number) => Math.round(value * 10) / 10

/**
 * HSV is how the ramp is specified; CSS only speaks HSL, and AG Charts wants a
 * plain colour string it can hand to a canvas.
 */
export const hsvToCss = (
  hue: number,
  saturation: number,
  value: number
): string => {
  const s = saturation / 100
  const v = value / 100
  const lightness = v * (1 - s / 2)
  const edge = Math.min(lightness, 1 - lightness)
  const hslSaturation = edge === 0 ? 0 : (v - lightness) / edge
  return `hsl(${round(hue)}, ${round(hslSaturation * 100)}%, ${round(lightness * 100)}%)`
}

/** Fill and outline for a dot at this ratio. */
export const dealDotColors = (
  ratio: number
): { fill: string; stroke: string } => {
  const hue = dealToHue(ratio)
  return {
    fill: hsvToCss(hue, DOT_SATURATION, DOT_VALUE),
    stroke: hsvToCss(hue, DOT_SATURATION, DOT_STROKE_VALUE),
  }
}

/**
 * Ratio width of one background band. The ramp's steepest segment is 0.80→0.90
 * at 20 hue per 0.10 of ratio, so this steps 2 degrees at a time — invisible at
 * the wash's low saturation, which is what lets a stack of bands read as a
 * gradient.
 */
const BAND_STEP = 0.01

/**
 * The background wash: a stack of thin crossLine ranges standing in for a
 * gradient.
 *
 * AG Charts has no gradient fill — a crossLine's `fill` is a solid colour, the
 * chart background takes only a colour or an image and spans the whole chart
 * rather than the plot, and the series area has no fill at all. Nor could any of
 * those be aligned to the plot even if they did, since no series rect is
 * exposed. Axis-space crossLines are the one thing that lands in the right place,
 * and they are clipped to the current Y domain for free — which is exactly the
 * behavior the bands need as the domain rescales.
 *
 * Static, because the thresholds are absolute ratios: this depends on neither the
 * data nor the domain in effect.
 */
const buildDealBands = (): AgCartesianCrossLineOptions<number>[] => {
  const [first] = HUE_KNOTS
  if (!first) return []

  const band = (
    from: number,
    to: number,
    hue: number
  ): AgCartesianCrossLineOptions<number> => ({
    type: 'range',
    range: [from, to],
    fill: hsvToCss(hue, BAND_SATURATION, BAND_VALUE),
    fillOpacity: BAND_OPACITY,
    strokeWidth: 0,
  })

  // One flat band under the first knot, matching `dealToHue`'s flat floor. Its
  // lower bound is 0 because a ratio cannot be negative, and the axis clips it.
  const bands = [band(0, first.ratio, first.hue)]

  const steps = Math.round((RAMP_TOP_RATIO - first.ratio) / BAND_STEP)
  for (let step = 0; step < steps; step++) {
    const from = first.ratio + step * BAND_STEP
    // The last edge is set outright rather than accumulated: adding the step 50
    // times lands just short of the threshold and leaves a hairline untinted.
    const to =
      step === steps - 1 ? RAMP_TOP_RATIO : first.ratio + (step + 1) * BAND_STEP
    bands.push(band(from, to, dealToHue((from + to) / 2)))
  }

  const last = HUE_KNOTS[HUE_KNOTS.length - 1]
  if (last) {
    // Flat above the last knot, mirroring the flat band below the first. The
    // upper bound is past any ratio the axis will render, and the axis clips it.
    bands.push(band(last.ratio, Number.MAX_SAFE_INTEGER, last.hue))
  }

  return bands
}

export const DEAL_BANDS = buildDealBands()
